import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { AlertTriangle, Lightbulb } from 'lucide-react';
import { cn } from '../../utils';

type CalloutProps = Omit<ComponentPropsWithoutRef<'div'>, 'title'> & {
  variant?: 'warning' | 'info';
  /** Omit for the variant default; pass null to render no icon. */
  icon?: ReactNode | null;
  title?: ReactNode;
  actions?: ReactNode;
  size?: 'sm' | 'md';
  children: ReactNode;
};

/** Shared advisory banner; information uses the website's editorial style. */
export function Callout({
  variant = 'warning',
  icon,
  title,
  actions,
  size = 'md',
  className,
  children,
  ...props
}: CalloutProps) {
  const info = variant === 'info';
  const inlineActions = size === 'sm' && title == null && actions != null;
  const resolvedIcon =
    icon !== undefined ? (
      icon
    ) : info ? (
      <Lightbulb />
    ) : (
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
    );
  if (!info && title == null && actions == null) {
    return (
      <div
        {...props}
        className={cn(
          'flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-800',
          size === 'sm' ? 'p-2' : 'p-3',
          className,
        )}
      >
        {resolvedIcon}
        {children}
      </div>
    );
  }
  const iconElement =
    resolvedIcon == null ? null : info ? (
      <span
        className={cn(
          'grid shrink-0 place-items-center rounded-xl border border-riso-blue/25 bg-white text-riso-blue shadow-[0_6px_14px_-10px_var(--color-riso-blue)] [&_svg]:m-0 [&_svg]:size-[19px] [&_svg]:stroke-[2.25]',
          size === 'sm' ? 'size-8' : 'size-[38px]',
        )}
      >
        {resolvedIcon}
      </span>
    ) : (
      resolvedIcon
    );
  return (
    <div
      {...props}
      className={cn(
        info
          ? 'relative isolate rounded-3xl border border-riso-blue/22 bg-riso-blue-light text-foreground shadow-[0_18px_40px_-26px_color-mix(in_srgb,var(--color-riso-blue)_42%,transparent)]'
          : 'rounded-lg border border-amber-300 bg-amber-50 text-sm text-amber-800',
        info
          ? size === 'sm'
            ? 'p-3 text-sm leading-relaxed'
            : 'p-6 text-[0.975rem] leading-[1.62] md:px-7 md:py-[26px]'
          : size === 'sm'
            ? 'p-2'
            : 'p-3',
        inlineActions && 'flex flex-wrap items-center gap-x-3 gap-y-2',
        className,
      )}
    >
      {info && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-[inherit]"
        >
          <span className="absolute -right-[30px] -top-[30px] size-[150px] rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,var(--color-riso-blue)_10%,transparent)_1px,transparent_1px)] bg-[length:9px_9px] mask-[radial-gradient(circle_at_center,black_38%,transparent_70%)]" />
        </span>
      )}
      {title != null ? (
        <>
          <div
            className={cn(
              'flex min-w-0 items-center gap-[13px]',
              size === 'sm' ? 'mb-2' : 'mb-4',
            )}
          >
            {iconElement}
            <div
              className={cn(
                'min-w-0 break-words font-display font-bold leading-tight',
                info && 'text-riso-blue',
                size === 'md' && 'text-[17px]',
              )}
            >
              {title}
            </div>
          </div>
          <div className="min-w-0 break-words">{children}</div>
        </>
      ) : (
        <div
          className={cn(
            'flex min-w-0 items-start',
            info ? 'gap-3' : 'gap-2',
            inlineActions && 'flex-1 basis-48',
          )}
        >
          {iconElement}
          <div className="min-w-0 flex-1 break-words">{children}</div>
        </div>
      )}
      {actions != null && (
        <div
          className={cn(
            'flex min-w-0 flex-wrap items-center gap-2 [&>a]:max-w-full [&>a]:whitespace-normal [&>button]:h-auto [&>button]:max-w-full [&>button]:whitespace-normal',
            !inlineActions && 'mt-3',
          )}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
